import { useState, useEffect, Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
    updateUserGuestQuotation
} from "../../../../../actions/admin/quotation";
import {
    getLocationByAddress
} from "../../../../../actions/googleAPI";
import ModalForm from "../../../../../components/modal/ModalForm";
import FormInput from "../../../../../components/input/FormInput";
import FormSelectHonrific from "../../../../../components/input/FormSelectHonrific";
import FormInputPhoneNumber from "../../../../../components/input/FormInputPhoneNumber";
import FormInputBillingAddress from "../../../../../components/input/FormInputBillingAddress";
import { EmailIcon } from "../../../../../components/icons";

const ClientInfoFormModal = ({
    show,
    onClose,
    onSuccessUpdateClient,
    onFailedUpdateClient,
    quotationDetail,
    buttonLabel,
    icon,
    isIconBeforeText
}) => {
    const { t } = useTranslation();
    const [addUserForm, setAddUserForm] = useState({
        type: "individual",
        honorific: "Mr.",
        name: "",
        email: "",
        prefixNumber: "+81",
        phoneNumber: "",
        country: "Japan",
        countryCode: "JP",
        postalCode: "16740",
        billingAddress: "",
        city: ""
    });
    const [isCityDisable, setIsCityDisable] = useState(true);
    const [isPostalCodeDisable, setIsPostalCodeDisable] = useState(false);
    
    // New validation flag for missing API data
    const [isPostalCodeNotFound, setIsPostalCodeNotFound] = useState(false);

    const [formValidation, setFormValidation] = useState({
        honorific: true,
        name: true,
        email: true,
        prefixNumber: true,
        phoneNumber: true,
        country: true,
        countryCode: true,
        postalCode: true,
        billingAddress: true,
        city: true,
    })
    const [romajiValidation, setRomajiValidation] = useState({
        name: true,
        billingAddress: true,
        city: true,
    })

    // 1. Load initial values when quotation detail updates
    useEffect(() => {
        const accountDetail = quotationDetail?.user?.[quotationDetail?.user?.role === "individual" ? "individualDetail" : "businessDetail"];
        setAddUserForm({
            honorific: accountDetail?.billingInformation?.pronounced,
            name: accountDetail?.billingInformation?.name,
            email: quotationDetail?.user?.email,
            prefixNumber: accountDetail?.billingInformation?.prefix_number,
            phoneNumber: accountDetail?.billingInformation?.phone,
            country: accountDetail?.billingInformation?.country,
            countryCode: accountDetail?.billingInformation?.country_code || "JP",
            postalCode: accountDetail?.billingInformation?.postal_code,
            billingAddress: accountDetail?.billingInformation?.address,
            city: accountDetail?.billingInformation?.city
        })
        // Reset custom errors when quotation detail changes
        setIsPostalCodeNotFound(false);
    }, [quotationDetail])

    // 2. NEW: Reactively handle UI form toggles based on countryCode shifts (handles both initial mount and selection updates)
    useEffect(() => {
        const countryWithoutPostCode = process.env.REACT_APP_COUNTRY_WITHOUT_POSTCODE || "";
        const list = countryWithoutPostCode.split(",");

        if (addUserForm.countryCode && list.includes(addUserForm.countryCode)) {
            setIsPostalCodeDisable(true);
            setIsCityDisable(false);
        } else {
            setIsPostalCodeDisable(false);
            setIsCityDisable(true);
        }
    }, [addUserForm.countryCode]);

    const checkValidation = async () => {
        const countryWithoutPostCode = process.env.REACT_APP_COUNTRY_WITHOUT_POSTCODE || "";
        const isCountryWithoutPostCode = countryWithoutPostCode.split(",").includes(addUserForm.countryCode);

        setFormValidation({
            honorific: addUserForm.honorific !== "",
            name: addUserForm.name !== "",
            email: addUserForm.email !== "",
            prefixNumber: addUserForm.prefixNumber !== "",
            phoneNumber: addUserForm.phoneNumber !== "",
            country: addUserForm.country !== "",
            countryCode: addUserForm.countryCode !== "",
            postalCode: isCountryWithoutPostCode ? true : addUserForm.postalCode !== "",
            billingAddress: addUserForm.billingAddress !== "",
            city: !isCountryWithoutPostCode ? true : addUserForm.city !== ""
        })

        return (
            addUserForm.honorific !== "" &&
            addUserForm.name !== "" &&
            romajiValidation.name &&
            addUserForm.email !== "" &&
            addUserForm.prefixNumber !== "" &&
            addUserForm.phoneNumber !== "" &&
            addUserForm.country !== "" &&
            addUserForm.countryCode !== "" &&
            (isCountryWithoutPostCode || addUserForm.postalCode !== "") &&
            !isPostalCodeNotFound &&
            addUserForm.billingAddress !== "" &&
            romajiValidation.billingAddress &&
            addUserForm.city !== "" &&
            romajiValidation.city
        )
    }

    const handleChangeNewClientForm = (e) => {
        let fieldName = e.target?.name ?? e.name;
        let fieldValue = e.target?.value ?? e.value;

        if (!fieldName) {
            console.warn("Field name missing in change event:", e);
            return;
        }

        if (fieldName === "postalCode") {
            setIsPostalCodeNotFound(false);
        }

        // romaji check
        if (["name", "billingAddress", "city"].includes(fieldName)) {
            const invalidChars = fieldValue
                .split("")
                .filter((char) => !/[\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(char));

            setRomajiValidation((prev) => ({
                ...prev,
                [fieldName]: invalidChars.length === 0,
            }));
        }

        // country code special value mutations
        if (fieldName === "countryCode") {
            const countryWithoutPostCode = process.env.REACT_APP_COUNTRY_WITHOUT_POSTCODE || "";

            if (!countryWithoutPostCode.split(",").includes(fieldValue)) {
                setAddUserForm((prev) => ({
                    ...prev,
                    countryCode: fieldValue,
                    postalCode: "",
                    city: "",
                }));
                return;
            } else {
                setAddUserForm((prev) => ({
                    ...prev,
                    countryCode: fieldValue,
                    postalCode: "-",
                    city: "",
                }));
                return;
            }
        }

        // finally update standard fields
        setAddUserForm((prev) => ({
            ...prev,
            [fieldName]: fieldValue,
        }));
    };

    const handleClickCountryList = (dialCode, countryCode, name) => {
        setAddUserForm({
            ...addUserForm,
            country: name,
            prefixNumber: dialCode,
        });

        handleChangeNewClientForm({ target: { name: "countryCode", value: countryCode } })
    };

    const updateUser = async () => {
        try {
            const validationStatus = await checkValidation();

            if (validationStatus) {
                const response = await updateUserGuestQuotation(addUserForm);
                const accountDetail = response?.data?.user[response?.data?.user?.role === "individual" ? "individualDetail" : "businessDetail"];
                setAddUserForm({
                    type: response?.data?.user?.role,
                    honorific: accountDetail?.billingInformation?.pronounced,
                    name: accountDetail?.billingInformation?.name,
                    email: response?.data?.user?.email,
                    prefixNumber: accountDetail?.billingInformation?.prefix_number,
                    phoneNumber: accountDetail?.billingInformation?.phone,
                    country: accountDetail?.billingInformation?.country,
                    countryCode: accountDetail?.billingInformation?.country_code,
                    postalCode: accountDetail?.billingInformation?.postal_code,
                    billingAddress: accountDetail?.billingInformation?.address,
                    city: accountDetail?.billingInformation?.city,
                });

                onSuccessUpdateClient(response?.data?.user);
            }
        } catch (e) {
            onFailedUpdateClient(e);
        }
    };

    const checkPostalCode = async () => {
        if (!addUserForm.postalCode || addUserForm.postalCode === "-") return;
        
        try {
            const zipCodeCheck = await getLocationByAddress(addUserForm.postalCode, addUserForm.countryCode);

            if (!zipCodeCheck || !zipCodeCheck.address_components) {
                setIsPostalCodeNotFound(true);
                return;
            }

            let city;
            if (zipCodeCheck.address_components.filter(item => item.types.includes("administrative_area_level_2") || item.types.includes("locality")).length !== 0) {
                city = zipCodeCheck.address_components.filter(item => item.types.includes("administrative_area_level_2") || item.types.includes("locality"))[0].long_name
            } else {
                city = zipCodeCheck.address_components[zipCodeCheck.address_components.length - 2].long_name
            }

            setAddUserForm({
                ...addUserForm,
                city
            });
            setIsPostalCodeNotFound(false);
        } catch (e) {
            setIsPostalCodeNotFound(true);
            console.error("Postal code search failed:", e);
        }
    };

    const checkPostalCodeHandler = async () => {
        await checkPostalCode();
    }

    return (
        <ModalForm
            show={show}
            onClose={onClose}
            title={t("Quotation.LinkPage.ClientInfoModal.Title")}
            description={t("Quotation.LinkPage.ClientInfoModal.Desc")}
        >
            <div className="tw:flex-1 tw:p-4">
                <div className="tw:flex tw:flex-wrap tw:mx-[calc(var(--gx)/-2)] tw:[--gx:1rem]">
                    <div className="tw:px-[calc(var(--gx)/2)] tw:mb-4 tw:w-full tw:flex-none tw:md:w-[16.6667%] tw:md:flex-none">
                        <FormSelectHonrific
                            onChange={(e) => handleChangeNewClientForm({ target: { name: "honorific", value: e.value } })}
                            placeholder="Honorific"
                            value={addUserForm.honorific}
                            className={"react-select-honorific tw:w-full"}
                        />
                    </div>
                    <div className="tw:px-[calc(var(--gx)/2)] tw:mb-4 tw:w-full tw:flex-none tw:md:w-[83.3333%] tw:md:flex-none">
                        <FormInput
                            type="text"
                            placeholder={t("Quotation.LinkPage.Form.Name")}
                            label={t("Quotation.LinkPage.Form.Name")}
                            value={addUserForm.name}
                            onChange={(e) => handleChangeNewClientForm(e)}
                            name="name"
                            isInvalid={!formValidation.name || !romajiValidation.name}
                            invalidMessage={
                                !romajiValidation.name ? "Kindly enter the client’s name and address using the Roman alphabet only (e.g., Tanaka Taro)"
                                    : !formValidation.name ? "Please fill in name."
                                        : ""
                            }
                        />
                    </div>
                </div>

                <div className="tw:flex tw:flex-wrap tw:mx-[calc(var(--gx)/-2)] tw:[--gx:1rem]">
                    <div className="tw:px-[calc(var(--gx)/2)] tw:mb-4 tw:w-full tw:flex-none tw:md:w-1/2 tw:md:flex-none">
                        <FormInput
                            placeholder={t("Quotation.LinkPage.Form.Email")}
                            label={t("Quotation.LinkPage.Form.Email")}
                            value={addUserForm.email}
                            onChange={(e) => handleChangeNewClientForm(e)}
                            name="email"
                            type="email"
                            isInvalid={!formValidation.email}
                            invalidMessage={"Please fill in email address."}
                            disabled={true}
                            icon={<EmailIcon />}
                        />
                    </div>
                    <div className="tw:px-[calc(var(--gx)/2)] tw:mb-4 tw:w-full tw:flex-none tw:md:w-1/2 tw:md:flex-none">
                        <FormInputPhoneNumber
                            placeholder={t("Quotation.LinkPage.Form.PhoneNo")}
                            label={t("Quotation.LinkPage.Form.PhoneNo")}
                            selectCountryPlaceholder={t("SelectCountry.Placeholder")}
                            value={addUserForm.phoneNumber}
                            countryCode={addUserForm.countryCode}
                            prefixPhoneNumber={addUserForm.prefixNumber}
                            onChange={(e) => handleChangeNewClientForm(e)}
                            handleClickCountryList={handleClickCountryList}
                            name="phoneNumber"
                            mb="tw:!mb-0 tw:!w-full"
                            isInvalid={!formValidation.phoneNumber}
                            invalidMessage={"Please fill in phone number."}
                        />
                    </div>
                </div>

                <div className="tw:flex tw:flex-wrap tw:mx-[calc(var(--gx)/-2)] tw:[--gx:1rem]">
                    <div className="tw:px-[calc(var(--gx)/2)] tw:mb-4 tw:w-full tw:flex-none tw:md:w-1/3 tw:md:flex-none">
                        <FormInput
                            placeholder={t("Quotation.LinkPage.Form.Country")}
                            label={t("Quotation.LinkPage.Form.Country")}
                            value={addUserForm.country}
                            name="country"
                            disabled={true}
                        />
                    </div>
                    <div className="tw:px-[calc(var(--gx)/2)] tw:mb-4 tw:w-full tw:flex-none tw:md:w-1/3 tw:md:flex-none">
                        <FormInput
                            placeholder={t("Quotation.LinkPage.Form.PostalCode")}
                            label={t("Quotation.LinkPage.Form.PostalCode")}
                            value={addUserForm.postalCode}
                            onChange={(e) => handleChangeNewClientForm(e)}
                            name="postalCode"
                            disabled={isPostalCodeDisable}
                            onBlur={checkPostalCodeHandler}
                            isInvalid={!formValidation.postalCode || isPostalCodeNotFound}
                            invalidMessage={
                                isPostalCodeNotFound 
                                    ? "Postal code not found. Please verify and try again." 
                                    : "Please fill in postal code."
                            }
                        />
                    </div>
                    <div className="tw:px-[calc(var(--gx)/2)] tw:mb-4 tw:w-full tw:flex-none tw:md:w-1/3 tw:md:flex-none">
                        <FormInput
                            placeholder={t("Quotation.LinkPage.Form.City")}
                            label={t("Quotation.LinkPage.Form.City")}
                            value={addUserForm.city}
                            onChange={(e) => handleChangeNewClientForm(e)}
                            name="city"
                            disabled={isCityDisable}
                            isInvalid={!formValidation.city || !romajiValidation.city}
                            invalidMessage={
                                !romajiValidation.city ? "Kindly enter the client’s city using the Roman alphabet only (e.g., Tanaka Taro)"
                                    : !formValidation.city ? "Please fill in city field."
                                        : ""
                            }
                        />
                    </div>
                </div>

                <div className="tw:flex tw:flex-wrap tw:mx-[calc(var(--gx)/-2)] tw:[--gx:1rem]">
                    <div className="tw:px-[calc(var(--gx)/2)] tw:w-full tw:flex-none">
                        <FormInputBillingAddress
                            placeholder={t("Quotation.LinkPage.Form.BillingAddress")}
                            label={t("Quotation.LinkPage.Form.BillingAddress")}
                            value={addUserForm.billingAddress}
                            onChange={(e) => handleChangeNewClientForm(e)}
                            name="billingAddress"
                            isInvalid={!formValidation.billingAddress || !romajiValidation.billingAddress}
                            invalidMessage={
                                !romajiValidation.billingAddress ? "Kindly enter the client’s name and address using the Roman alphabet only (e.g., Tanaka Taro)"
                                    : !formValidation.billingAddress ? "Please fill in billing address."
                                        : ""
                            }
                        />
                    </div>
                </div>

                <div className="tw:grid tw:gap-2 tw:px-[calc(var(--gx)/2)] tw:w-full tw:flex-none tw:md:w-1/3 tw:md:flex-none tw:mx-auto tw:mt-6">
                    <button
                        className="bg-gold tw:rounded-[0.375rem]! tw:w-full"
                        onClick={updateUser}
                    >
                        <div className="tw:flex tw:gap-2 tw:px-4 tw:py-2 tw:justify-center tw:items-center">
                            {icon && isIconBeforeText && icon}
                            <p className="tw:m-0! tw:text-white!">{buttonLabel}</p>
                            {icon && !isIconBeforeText && icon}
                        </div>
                    </button>
                </div>
            </div>
        </ModalForm>
    )
}

export default ClientInfoFormModal;